package com.pincer.core.model.patches

import com.pincer.core.InvalidData
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerializationException
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.longOrNull

/*
A change to a Pincer, as a JSON Merge Patch (RFC 7396): an object shaped like the Pincer, where an absent key
means "no change", null means "delete", and nested objects merge. See MergePatch for the mechanics.

Patches are made by diffing two Pincers (Pincer.diff) rather than built by hand, so there's no patch code to
write when the model gains a field. The format on the wire and in the stores is the same one the hand-written
patch classes produced before September 2026, so stored patches keep their meaning.

The version fields (sid, sidParent, sidPrincipal) get typed accessors because the server stamps them onto every
patch. Everything else is read through paths, e.g. patch.has("participants", id, "redRules").
*/
@Serializable(with = PincerPatchSerializer::class)
class PincerPatch(json: JsonObject = JsonObject(emptyMap())) {

    var json: JsonObject = json
        private set

    companion object {
        fun parse(json: String): PincerPatch {
            if (json.indexOf("<") > -1) throw InvalidData("The left angle bracket is not allowed in any pincer patch.")
            val element = try { Json.parseToJsonElement(json) }
                          catch (se: SerializationException) { throw InvalidData("There's a problem with the PincerPatch json") }
            if (element !is JsonObject) throw InvalidData("A PincerPatch must be a JSON object")
            return PincerPatch(element)
        }
    }

    fun json(): String = json.toString()

    fun isEmpty() = json.isEmpty()

    /** The value at a path, or null if the patch doesn't touch it. A deletion comes back as JsonNull. */
    fun get(vararg path: String): JsonElement? {
        var cursor: JsonElement = json
        for (key in path) cursor = (cursor as? JsonObject)?.get(key) ?: return null
        return cursor
    }

    /** Whether the patch touches this path (sets, changes or deletes it, or anything inside it). */
    fun has(vararg path: String) = get(*path) != null

    /** The keys the patch touches in the object at a path, e.g. keys("participants"). */
    fun keys(vararg path: String): Set<String> = (get(*path) as? JsonObject)?.keys ?: emptySet()

    fun string(vararg path: String): String? = (get(*path) as? JsonPrimitive)?.contentOrNull
    fun long(vararg path: String): Long? = (get(*path) as? JsonPrimitive)?.longOrNull
    fun boolean(vararg path: String): Boolean? = (get(*path) as? JsonPrimitive)?.booleanOrNull
    fun isDeletion(vararg path: String) = get(*path) is JsonNull

    fun set(key: String, value: JsonElement?) {
        json = JsonObject(json.toMutableMap().also { if (value == null) it.remove(key) else it[key] = value })
    }

    var sid: Long?
        get() = long("sid")
        set(value) = set("sid", value?.let { JsonPrimitive(it) })

    var sidParent: Long?
        get() = long("sidParent")
        set(value) = set("sidParent", value?.let { JsonPrimitive(it) })

    var sidPrincipal: String?
        get() = string("sidPrincipal")
        set(value) = set("sidPrincipal", value?.let { JsonPrimitive(it) })

    var candidatesStale: Boolean?
        get() = boolean("candidatesStale")
        set(value) = set("candidatesStale", value?.let { JsonPrimitive(it) })

    fun includesRuleChange(): Boolean {
        if (has("rules")) return true
        for (participantId in keys("participants")) {
            if (isDeletion("participants", participantId)) return true
            if (has("participants", participantId, "redRules")) return true
            if (has("participants", participantId, "greenRules")) return true
        }
        return false
    }

    override fun equals(other: Any?) = other is PincerPatch && other.json == json
    override fun hashCode() = json.hashCode()
    override fun toString() = "PincerPatch(${json()})"

}

object PincerPatchSerializer : KSerializer<PincerPatch> {
    override val descriptor: SerialDescriptor = JsonObject.serializer().descriptor
    override fun serialize(encoder: Encoder, value: PincerPatch) = JsonObject.serializer().serialize(encoder, value.json)
    override fun deserialize(decoder: Decoder) = PincerPatch(JsonObject.serializer().deserialize(decoder))
}
